import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { subscribeToAuthChanges } from '../lib/auth';
import { db, auth } from '../lib/firebase';
import { Therapist } from '../types/firestoreTypes';

// Interface for the context value
interface AuthContextType {
  user: User | null;
  therapist: Therapist | null;
  loading: boolean;
  therapistLoaded: boolean;
  error: string | null; // Error will now store the translated string
  isAdmin: boolean;
  adminChecked: boolean;
  refreshAdminStatus: () => Promise<void>;
  statusFromClaims: string | null;
  currentPackageIdFromClaims: string | null;
  packageExpiryDateFromClaims: number | null;
}

// Default context value
const AuthContext = createContext<AuthContextType>({
  user: null,
  therapist: null,
  loading: true,
  therapistLoaded: false,
  error: null,
  isAdmin: false,
  adminChecked: false,
  refreshAdminStatus: async () => {},
  statusFromClaims: null,
  currentPackageIdFromClaims: null,
  packageExpiryDateFromClaims: null,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation(); // Get the t function
  const [user, setUser] = useState<User | null>(null);
  const [therapist, setTherapist] = useState<Therapist | null>(null);
  const [loading, setLoading] = useState(true);
  const [therapistLoaded, setTherapistLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null); // State holds the final translated string
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminChecked, setAdminChecked] = useState(false);
  const [statusFromClaims, setStatusFromClaims] = useState<string | null>(null);
  const [currentPackageIdFromClaims, setCurrentPackageIdFromClaims] = useState<string | null>(null);
  const [packageExpiryDateFromClaims, setPackageExpiryDateFromClaims] = useState<number | null>(null);
  const isProcessingAuthChange = useRef(false);

  // Function to set translated error messages
  const setTranslatedError = (key: string, options?: object) => {
    setError(t(key, options));
  };

  // Refresh admin status logic remains the same, but uses translated errors
  const refreshAdminStatus = useCallback(async () => {
    if (!auth.currentUser) {
      setIsAdmin(false);
      setAdminChecked(true);
      return;
    }
    setAdminChecked(false); // Indicate checking is in progress
    try {
      const idTokenResult = await auth.currentUser.getIdTokenResult(true); // Force refresh
      setIsAdmin(!!idTokenResult.claims.isAdmin);
    } catch (error) {
      console.error('Error refreshing admin status:', error);
      setTranslatedError('authContext.errors.permissionCheckFailed'); // Use key
      setIsAdmin(false); // Ensure admin is false on error
    } finally {
      setAdminChecked(true); // Mark as checked regardless of outcome
    }
  }, [t]); // Add t to dependencies

  // Main effect for handling auth changes and profile loading
  useEffect(() => {
    let unsubscribeTherapist: (() => void) | undefined;

    const unsubscribeAuth = subscribeToAuthChanges(async (authUser) => {
      if (isProcessingAuthChange.current) {
        console.log('Auth change processing already in progress, skipping.');
        return;
      }
      isProcessingAuthChange.current = true;
      console.log('Processing auth change for user:', authUser?.uid);

      // Reset state on auth change
      setUser(authUser);
      setError(null);
      setTherapist(null);
      setTherapistLoaded(false);
      setIsAdmin(false);
      setAdminChecked(false);
      setStatusFromClaims(null);
      setCurrentPackageIdFromClaims(null);
      setPackageExpiryDateFromClaims(null);
      setLoading(true);

      // Cleanup previous therapist listener if exists
      if (typeof unsubscribeTherapist === 'function') {
        console.log('Unsubscribing previous therapist listener.');
        unsubscribeTherapist();
        unsubscribeTherapist = undefined;
      }

      // Handle signed-out state
      if (!authUser) {
        console.log('User signed out.');
        setLoading(false);
        setAdminChecked(true); // Considered checked (no admin)
        setTherapistLoaded(true); // Considered loaded (no therapist)
        setStatusFromClaims(null);
        setCurrentPackageIdFromClaims(null);
        setPackageExpiryDateFromClaims(null);
        isProcessingAuthChange.current = false; // Release lock
        return;
      }

      // Handle signed-in state
      try {
        console.log(
          'User signed in, checking admin status and loading profile...'
        );
        // Check admin status first
        const idTokenResult = await authUser.getIdTokenResult(true); // Force refresh token
        const isAdminUser = !!idTokenResult.claims.isAdmin;
        setIsAdmin(isAdminUser);
        setAdminChecked(true);
        console.log('Admin status checked:', isAdminUser);

        // Extract and set custom claims
        const claims = idTokenResult.claims;
        setStatusFromClaims(claims.status as string || null);
        setCurrentPackageIdFromClaims(claims.currentPackageId as string || null);
        // Ensure packageExpiryDate is a number or null
        const expiryDate = claims.packageExpiryDate;
        setPackageExpiryDateFromClaims(typeof expiryDate === 'number' ? expiryDate : null);

        console.log('Custom claims extracted:', {
          status: claims.status,
          packageId: claims.currentPackageId,
          expiryMs: typeof expiryDate === 'number' ? expiryDate : null,
        });

        // Subscribe to therapist profile changes
        if (authUser.uid && db) {
          const therapistDocRef = doc(db, 'therapists', authUser.uid);
          console.log('Subscribing to therapist document:', authUser.uid);
          unsubscribeTherapist = onSnapshot(
            therapistDocRef,
            (docSnapshot) => {
              console.log('Therapist snapshot received:', docSnapshot.exists());
              if (docSnapshot.exists()) {
                setTherapist({
                  id: docSnapshot.id,
                  ...(docSnapshot.data() as Omit<Therapist, 'id'>),
                });
              } else {
                console.log('Therapist document does not exist.');
                setTherapist(null);
                // Optionally set an error if a therapist doc is expected but missing
                // setTranslatedError('authContext.errors.profileNotFound');
              }
              setTherapistLoaded(true);
              setLoading(false); // Loading complete after profile check
            },
            (err) => {
              // Handle Firestore read error
              console.error(
                'Auth Listener: Error fetching therapist profile:',
                err
              );
              setTranslatedError('authContext.errors.profileLoadFailed'); // Use key
              setTherapist(null);
              setTherapistLoaded(true); // Still considered 'loaded' (with error)
              setLoading(false);
            }
          );
        } else {
          // Should not happen if authUser exists, but handle defensively
          console.warn('Auth user exists but UID or DB is missing.');
          setTherapistLoaded(true);
          setLoading(false);
        }
      } catch (error) {
        // Handle errors during token refresh or initial setup
        console.error('Auth Listener: Error during auth processing:', error);
        setTranslatedError('authContext.errors.authProcessingFailed'); // Use key
        setIsAdmin(false); // Reset admin on error
        setAdminChecked(true); // Mark as checked (with error)
        setTherapistLoaded(true); // Mark as loaded (with error)
        setStatusFromClaims(null);
        setCurrentPackageIdFromClaims(null);
        setPackageExpiryDateFromClaims(null);
        setLoading(false);
      } finally {
        // Ensure the lock is always released
        isProcessingAuthChange.current = false;
        console.log('Finished processing auth change.');
      }
    });

    // Cleanup function for the main auth listener
    return () => {
      console.log('Cleaning up AuthProvider listeners.');
      unsubscribeAuth();
      if (typeof unsubscribeTherapist === 'function') {
        unsubscribeTherapist();
      }
    };
  }, [t]); // Add trackEvent to dependencies

  // Context value provided to consumers
  const value = {
    user,
    therapist,
    loading,
    therapistLoaded,
    error, // Provides the translated error string
    isAdmin,
    adminChecked,
    refreshAdminStatus,
    statusFromClaims,
    currentPackageIdFromClaims,
    packageExpiryDateFromClaims,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
